//#region src/utils/tableColor.ts
var AML_COLORS = {
	red: "#ef4444",
	orange: "#f97316",
	amber: "#f59e0b",
	yellow: "#eab308",
	lime: "#84cc16",
	green: "#22c55e",
	emerald: "#10b981",
	teal: "#14b8a6",
	cyan: "#06b6d4",
	sky: "#0ea5e9",
	blue: "#3b82f6",
	indigo: "#6366f1",
	violet: "#8b5cf6",
	purple: "#a855f7",
	fuchsia: "#d946ef",
	pink: "#ec4899",
	rose: "#f43f5e",
	gray: "#6b7280"
};
var DBML_HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
var AML_HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
var ANY_HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
var COLOR_FUNCTION = /^(rgba?|hsla?)\(([^()]*)\)$/i;
var ARGUMENT_SEPARATOR = /\s*[,/]\s*|\s+/;
var NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;
var ANGLE_UNIT = /deg$/i;
var PERCENT = /%$/;
var AML_COLOR_BY_NAME = new Map(Object.entries(AML_COLORS));
var GRAY_CHROMA = .04;
function fromDBMLColor(value) {
	return DBML_HEX.test(value) ? value : "";
}
function fromAMLColor(value) {
	return AML_COLOR_BY_NAME.get(value.toLowerCase()) ?? (AML_HEX.test(value) ? value : "");
}
function toDBMLColor(color) {
	const value = color.trim();
	if (ANY_HEX.test(value)) return value.length - 1 === 4 ? value.slice(0, 4) : value.slice(0, 7);
	const channels = channelsOf(value);
	return channels ? toHex(channels) : null;
}
function toAMLColor(color) {
	const channels = channelsOf(color.trim());
	if (!channels) return null;
	const [, a, b] = oklabOf(channels);
	if (Math.hypot(a, b) < GRAY_CHROMA) return "gray";
	const hue = Math.atan2(b, a);
	let nearest = null;
	let nearestDistance = Infinity;
	for (const [name, hex] of AML_COLOR_BY_NAME) {
		if (name === "gray") continue;
		const [, namedA, namedB] = oklabOf(hexChannels(hex.slice(1)));
		const turn = Math.abs(hue - Math.atan2(namedB, namedA));
		const distance = Math.min(turn, 2 * Math.PI - turn);
		if (distance < nearestDistance) {
			nearest = name;
			nearestDistance = distance;
		}
	}
	return nearest;
}
function toOpaqueHex(color) {
	const channels = channelsOf(color.trim());
	return channels ? toHex(channels) : null;
}
function channelsOf(value) {
	if (ANY_HEX.test(value)) return hexChannels(value.slice(1));
	const match = COLOR_FUNCTION.exec(value);
	if (!match) return null;
	const args = match[2].trim().split(ARGUMENT_SEPARATOR);
	if (args.length < 3) return null;
	return match[1].toLowerCase().startsWith("rgb") ? rgbChannels(args) : hslChannels(args);
}
function hexChannels(digits) {
	const full = digits.length <= 4 ? [...digits].map((digit) => `${digit}${digit}`).join("") : digits;
	return [
		0,
		2,
		4
	].map((offset) => parseInt(full.slice(offset, offset + 2), 16));
}
function rgbChannels(args) {
	const channels = [];
	for (const arg of args.slice(0, 3)) {
		const percent = arg.endsWith("%");
		const value = numberOf(percent ? arg.slice(0, -1) : arg);
		if (value === null) return null;
		channels.push(clampChannel(percent ? value * 2.55 : value));
	}
	return channels;
}
function hslChannels(args) {
	const hue = numberOf(args[0].replace(ANGLE_UNIT, ""));
	const saturation = numberOf(args[1].replace(PERCENT, ""));
	const lightness = numberOf(args[2].replace(PERCENT, ""));
	if (hue === null || saturation === null || lightness === null) return null;
	if (!Number.isFinite(hue)) return null;
	const h = (hue % 360 + 360) % 360;
	const s = Math.min(Math.max(saturation, 0), 100) / 100;
	const l = Math.min(Math.max(lightness, 0), 100) / 100;
	const a = s * Math.min(l, 1 - l);
	const f = (n) => {
		const k = (n + h / 30) % 12;
		return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
	};
	return [
		f(0),
		f(8),
		f(4)
	].map((value) => clampChannel(value * 255));
}
function oklabOf(channels) {
	const [r, g, b] = channels.map(toLinear);
	const l = Math.cbrt(.4122214708 * r + .5363325363 * g + .0514459929 * b);
	const m = Math.cbrt(.2119034982 * r + .6806995451 * g + .1073969566 * b);
	const s = Math.cbrt(.0883024619 * r + .2817188376 * g + .6299787005 * b);
	return [
		.2104542553 * l + .793617785 * m - .0040720468 * s,
		1.9779984951 * l - 2.428592205 * m + .4505937099 * s,
		.0259040371 * l + .7827717662 * m - .808675766 * s
	];
}
function toLinear(channel) {
	const value = channel / 255;
	return value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
}
function numberOf(text) {
	return NUMBER.test(text) ? Number(text) : null;
}
function clampChannel(value) {
	return Math.round(Math.min(Math.max(value, 0), 255));
}
function toHex(channels) {
	return `#${channels.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}
//#endregion
export { AML_COLORS, fromAMLColor, fromDBMLColor, toAMLColor, toDBMLColor, toLinear, toOpaqueHex };
